import React, { useState, useEffect } from 'react';
import { User, Edit, Loader, Mail, Phone, MapPin } from 'lucide-react';
import { peopleService } from '../../modules/people/peopleService';
import { EditPersonModal } from '../../modules/people/EditPersonModal';
import { useAuth } from '../../hooks/useAuth';
import { Person } from '../../types';
import { FirebaseImage } from '../shared/FirebaseImage';

interface ProfilePageProps {
  onProfileUpdate?: () => void;
}

export const ProfilePage: React.FC<ProfilePageProps> = ({ onProfileUpdate }) => {
  const { appUser } = useAuth();
  const [person, setPerson] = useState<Person | null>(null);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadPersonProfile();
  }, [appUser]);

  const loadPersonProfile = async () => {
    if (!appUser?.email) return;
    
    try {
      setLoading(true);
      const personData = await peopleService.getPersonByEmail(appUser.email);
      setPerson(personData);
      
      if (!personData) {
        setError('Profile not found. Please complete your profile setup.');
      }
    } catch (error) {
      console.error('Failed to load profile:', error);
      setError('Failed to load profile data.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditSuccess = () => {
    loadPersonProfile(); // Refresh profile data
    setShowEditModal(false);
    // Notify parent component to refresh user data in header
    onProfileUpdate?.();
  };

  // Generate initials for avatar fallback
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="animate-spin" size={32} />
        <span className="ml-2">Loading profile...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-yellow-800 mb-2">Profile Setup Required</h2>
          <p className="text-yellow-700 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()} // This will trigger the onboarding flow
            className="bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90"
          >
            Complete Profile Setup
          </button>
        </div>
      </div>
    );
  }

  if (!person) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
          <User className="mx-auto h-16 w-16 text-gray-400 mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">No Profile Found</h2>
          <p className="text-gray-600">Your profile hasn't been set up yet.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Profile Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-start mb-6">
          <div className="flex items-center gap-6">
            {/* Profile Picture */}
            <div className="relative">
              <FirebaseImage
                src={person.profilePictureUrl}
                storagePath={person.profilePicturePath}
                alt={person.name}
                className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg"
                fallback={
                  <div className="w-24 h-24 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-2xl font-semibold border-4 border-white shadow-lg">
                    {getInitials(person.name)}
                  </div>
                }
              />
            </div>

            {/* Basic Info */}
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{person.name}</h1>
              <p className="text-xl text-primary font-medium">{person.title}</p>
              <div className="flex items-center gap-4 mt-2">
                {person.department && (
                  <div className="flex items-center gap-1 text-gray-600">
                    <MapPin size={16} />
                    <span>{person.department}</span>
                  </div>
                )}
                <div className="flex items-center gap-1 text-gray-600">
                  <Mail size={16} />
                  <span>{person.email}</span>
                </div>
                {person.phone && (
                  <div className="flex items-center gap-1 text-gray-600">
                    <Phone size={16} />
                    <span>{person.phone}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Edit Button */}
          <button
            onClick={() => setShowEditModal(true)}
            className="bg-primary text-primary-foreground px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-primary/90"
          >
            <Edit size={16} />
            Edit Profile
          </button>
        </div>

        {/* Bio */}
        {person.bio && (
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold mb-3">About</h3>
            <p className="text-gray-700 leading-relaxed">{person.bio}</p>
          </div>
        )}
      </div>

      {/* Skills and Experience */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Work Tasks */}
        {person.arbeidsoppgaver && (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Work Tasks</h3>
            <p className="text-gray-700 leading-relaxed">{person.arbeidsoppgaver}</p>
          </div>
        )}

        {/* Other Skills */}
        {person.andreFerdigheter && (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Other Skills & Expertise</h3>
            <p className="text-gray-700 leading-relaxed">{person.andreFerdigheter}</p>
          </div>
        )}
      </div>

      {/* Technical Skills */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-6">Technical Skills & Tools</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Object.entries({
            'Tools & Systems': person.verktoyOgSystemer,
            'Developer Tools': person.spesialiserteVerktoyUtvikler,
            'Design Tools': person.spesialiserteVerktoyDesigner,
            'Marketing Tools': person.spesialiserteVerktoyMarkedsforing,
            'Project Management': person.spesialiserteVerktoyProsjektledelse,
            '3D & Animation': person.spesialiserteVerktoy3D,
            'AI Tools': person.spesialiserteVerktoyAI,
          }).map(([category, skills]) => (
            skills.length > 0 && (
              <div key={category} className="space-y-3">
                <h4 className="font-medium text-gray-900 text-sm uppercase tracking-wide">
                  {category}
                </h4>
                <div className="flex flex-wrap gap-2">
                  {skills.map(skill => (
                    <span 
                      key={skill}
                      className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )
          ))}
        </div>

        {/* Show message if no skills */}
        {!Object.values({
          verktoyOgSystemer: person.verktoyOgSystemer,
          spesialiserteVerktoyUtvikler: person.spesialiserteVerktoyUtvikler,
          spesialiserteVerktoyDesigner: person.spesialiserteVerktoyDesigner,
          spesialiserteVerktoyMarkedsforing: person.spesialiserteVerktoyMarkedsforing,
          spesialiserteVerktoyProsjektledelse: person.spesialiserteVerktoyProsjektledelse,
          spesialiserteVerktoy3D: person.spesialiserteVerktoy3D,
          spesialiserteVerktoyAI: person.spesialiserteVerktoyAI,
        }).some(skills => skills.length > 0) && (
          <div className="text-center py-8 text-gray-500">
            <p>No technical skills added yet.</p>
            <button
              onClick={() => setShowEditModal(true)}
              className="text-primary hover:underline mt-2"
            >
              Add your skills and tools
            </button>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {showEditModal && person && (
        <EditPersonModal
          isOpen={showEditModal}
          person={person}
          onClose={() => setShowEditModal(false)}
          onSuccess={handleEditSuccess}
        />
      )}
    </div>
  );
};